/**
 * Pure logic for the user <-> session index at `users/{uid}/sessions/{eventId}`.
 *
 * The index stores metadata only. Event documents remain the single source of
 * truth for title, participants, queue and player state, and are never copied
 * here or deleted.
 */

export const SESSION_ROLE = {
  host: 'host',
  guest: 'guest',
};

export const SESSION_STATUS = {
  active: 'active',
  previous: 'previous',
};

export const SESSION_MEMBERSHIP = {
  left: 'left',
};

/**
 * A guest who used Leave Party is still historically associated with the
 * event, but this private index row is no longer a live-session pointer.
 */
export function isIntentionalLeave(entry) {
  if (!entry) return false;
  return entry.status === SESSION_MEMBERSHIP.left || entry.leftAt != null;
}

/**
 * A session with no recent activity is shown under "Previous".
 * An explicit `endedAt` / `status: 'ended'` on the event (or copied onto the
 * private index) also forces Previous, including for guests who were offline
 * when the host ended the party.
 */
export const SESSION_ACTIVE_WINDOW_MS = 12 * 60 * 60 * 1000;

/** Copy lifecycle fields from the event onto the session index, nothing else. */
export function sessionLifecycleFields(eventData) {
  if (!eventData) return {};
  if (eventData.status === 'ended' || eventData.endedAt) {
    return {
      status: 'ended',
      endedAt: eventData.endedAt ?? null,
    };
  }
  return {};
}

/**
 * Overlay event-document lifecycle onto a private session-index row.
 * The event remains the source of truth; this never copies queue/participants.
 */
export function applyEventLifecycleToSession(session, eventData) {
  if (!session) return session;
  const lifecycle = sessionLifecycleFields(eventData);
  if (!lifecycle.status) return session;
  return { ...session, ...lifecycle };
}

/** Firestore Timestamp, Date, ISO string or epoch millis -> epoch millis. */
export function toMillis(value) {
  if (value == null) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value?.toMillis === 'function') {
    try {
      return value.toMillis();
    } catch {
      return null;
    }
  }
  if (typeof value?.seconds === 'number') return value.seconds * 1000;
  if (value instanceof Date) {
    const time = value.getTime();
    return Number.isFinite(time) ? time : null;
  }
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

export function normalizeSessionRole(role) {
  return role === SESSION_ROLE.host ? SESSION_ROLE.host : SESSION_ROLE.guest;
}

/**
 * Metadata-only index entry. `eventId` doubles as the document id, which makes
 * repeated joins idempotent without a query.
 */
export function buildSessionEntry({
  eventId,
  title = '',
  role = SESSION_ROLE.guest,
  createdAt = null,
  timestamp = null,
  endedAt = null,
  status = null,
  leftAt = null,
} = {}) {
  if (!eventId || typeof eventId !== 'string') return null;

  const entry = {
    eventId,
    title: typeof title === 'string' ? title : '',
    role: normalizeSessionRole(role),
    createdAt: createdAt ?? timestamp,
    joinedAt: timestamp,
    lastSeenAt: timestamp,
    updatedAt: timestamp,
  };

  if (status === SESSION_MEMBERSHIP.left || leftAt) {
    entry.status = SESSION_MEMBERSHIP.left;
    entry.leftAt = leftAt ?? timestamp;
    return entry;
  }

  if (status === 'ended' || endedAt) {
    entry.status = 'ended';
    entry.endedAt = endedAt ?? timestamp;
  }

  return entry;
}

/** Touch payload for an existing entry; never rewrites joinedAt/createdAt. */
export function buildSessionTouch({
  title = null,
  role = null,
  timestamp = null,
  endedAt = null,
  status = null,
  leftAt = null,
} = {}) {
  const payload = { lastSeenAt: timestamp, updatedAt: timestamp };
  if (typeof title === 'string' && title) payload.title = title;
  if (role) payload.role = normalizeSessionRole(role);
  if (status === SESSION_MEMBERSHIP.left || leftAt) {
    payload.status = SESSION_MEMBERSHIP.left;
    payload.leftAt = leftAt ?? timestamp;
    return payload;
  }
  if (status === 'ended' || endedAt) {
    payload.status = 'ended';
    payload.endedAt = endedAt ?? timestamp;
  }
  return payload;
}

export function deriveSessionStatus(entry, { now = Date.now(), activeWindowMs = SESSION_ACTIVE_WINDOW_MS } = {}) {
  if (!entry) return SESSION_STATUS.previous;
  if (entry.endedAt || entry.status === 'ended' || entry.status === SESSION_STATUS.previous) {
    return SESSION_STATUS.previous;
  }
  if (isIntentionalLeave(entry)) return SESSION_STATUS.previous;

  const lastSeen = toMillis(entry.lastSeenAt) ?? toMillis(entry.joinedAt) ?? toMillis(entry.createdAt);
  if (lastSeen == null) return SESSION_STATUS.previous;

  return now - lastSeen <= activeWindowMs ? SESSION_STATUS.active : SESSION_STATUS.previous;
}

function sortKey(entry) {
  return toMillis(entry?.lastSeenAt) ?? toMillis(entry?.joinedAt) ?? toMillis(entry?.createdAt) ?? 0;
}

/** Splits entries into active/previous, newest first, without mutating input. */
export function partitionSessions(entries, options = {}) {
  const list = Array.isArray(entries) ? entries.filter(Boolean) : [];
  const sorted = [...list].sort((a, b) => sortKey(b) - sortKey(a));

  return sorted.reduce(
    (accumulator, entry) => {
      const status = deriveSessionStatus(entry, options);
      accumulator[status === SESSION_STATUS.active ? 'active' : 'previous'].push({ ...entry, status });
      return accumulator;
    },
    { active: [], previous: [] },
  );
}

export function formatSessionTimestamp(entry, { locale = undefined } = {}) {
  const millis = toMillis(entry?.joinedAt) ?? toMillis(entry?.createdAt) ?? toMillis(entry?.lastSeenAt);
  if (millis == null) return '';
  try {
    return new Date(millis).toLocaleString(locale, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return new Date(millis).toISOString();
  }
}
